import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Layers, Boxes, Sparkles, FileCode, Plus, Check, Eye, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const tierIcon = { standard: Layers, "3d": Boxes, premium: Sparkles };

const DESIGN_W = 1280;
const DESIGN_H = Math.round((DESIGN_W * 10) / 16);

// Real, scaled live-render thumbnail of the actual template (lazy-loaded, static).
export const TemplateThumb = ({ t, className = "" }) => {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.24);
  const [html, setHtml] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w) setScale(w / DESIGN_W);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || html) return;
    let alive = true;
    api.get(`/templates/${t.slug}/preview`)
      .then(({ data }) => {
        if (!alive) return;
        // Static thumbnail: kill heavy animations to keep the grid light.
        const doc = String(data.html || "").replace(
          "</head>",
          "<style>*{animation:none!important}html{scroll-behavior:auto!important}</style></head>"
        );
        setHtml(doc);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [visible, t.slug, html]);

  return (
    <div ref={wrapRef} className={`relative overflow-hidden bg-white ${className}`} data-testid={`thumb-${t.slug}`}>
      {!html && (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${t.color_primary}, ${t.color_secondary})` }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
          <div className="absolute inset-0 grid place-items-center"><Loader2 className="h-6 w-6 text-white/80 animate-spin" /></div>
        </div>
      )}
      {html && (
        <iframe title={`${t.name} preview thumbnail`} srcDoc={html} scrolling="no" tabIndex={-1} loading="lazy"
          sandbox="allow-scripts allow-same-origin"
          style={{ width: DESIGN_W, height: DESIGN_H, transform: `scale(${scale})`, transformOrigin: "top left", border: 0, pointerEvents: "none", display: "block" }} />
      )}
      <span className="absolute top-3 right-3 text-[10px] font-bold text-white/95 bg-black/35 rounded-full px-2 py-0.5 backdrop-blur pointer-events-none">
        {t.scheme_name}
      </span>
    </div>
  );
};

export const TemplatePreview = ({ t, className = "" }) => (
  <div className={`relative overflow-hidden ${className}`}
    style={{ background: `linear-gradient(135deg, ${t.color_primary}, ${t.color_secondary})` }}>
    <div className="absolute inset-0 opacity-20"
      style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
    <div className="absolute inset-x-4 top-4 bottom-0 rounded-t-xl bg-white/95 shadow-2xl">
      <div className="h-6 flex items-center gap-1.5 px-3 border-b border-black/5">
        <span className="h-2 w-2 rounded-full bg-red-400" />
        <span className="h-2 w-2 rounded-full bg-amber-400" />
        <span className="h-2 w-2 rounded-full bg-green-400" />
      </div>
      <div className="p-3">
        <div className="h-3 w-1/2 rounded" style={{ background: t.color_primary }} />
        <div className="mt-2 h-2 w-3/4 rounded bg-black/10" />
        <div className="mt-1.5 h-2 w-2/3 rounded bg-black/10" />
        <div className="mt-3 flex gap-2">
          <div className="h-8 w-16 rounded" style={{ background: t.color_primary }} />
          <div className="h-8 w-12 rounded bg-black/10" />
        </div>
      </div>
    </div>
    <span className="absolute top-3 right-3 text-[10px] font-bold text-white/90 bg-black/25 rounded-full px-2 py-0.5 backdrop-blur">
      {t.scheme_name}
    </span>
  </div>
);

export const TemplateCard = ({ t }) => {
  const { add, has } = useCart();
  const Icon = tierIcon[t.tier] || FileCode;
  const inCart = has("template", t.slug);
  const addToCart = (e) => {
    e.preventDefault();
    add({ kind: "template", id: t.slug, name: t.name, price: t.price, meta: `${t.type_label} · ${t.tier_label}` });
  };
  return (
    <div className="group rounded-2xl glass overflow-hidden hover:-translate-y-1 transition-transform duration-300 flex flex-col" data-testid={`template-card-${t.slug}`}>
      <div className="relative">
        <Link to={`/templates/${t.slug}`}>
          <TemplateThumb t={t} className="aspect-[16/10]" />
        </Link>
        <Link to={`/templates/${t.slug}/preview`} data-testid={`preview-${t.slug}`}
          className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-4 h-10 font-semibold text-sm shadow-lg"><Eye className="h-4 w-4" /> Live Preview</span>
        </Link>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 font-semibold"><Icon className="h-3 w-3" /> {t.tier_label}</span>
          <span className="text-muted-foreground">{t.type_label}</span>
          <span className="text-muted-foreground">· {t.pages}p</span>
        </div>
        <Link to={`/templates/${t.slug}`}><h3 className="font-head font-bold text-lg mt-2 leading-snug hover:text-primary transition-colors">{t.name}</h3></Link>
        <p className="text-xs text-muted-foreground mt-1">{t.business_type} website</p>
        <div className="mt-auto pt-3 flex items-center gap-2">
          <span className="mr-auto flex items-baseline gap-1.5">
            <span className="font-head font-extrabold text-xl text-primary">${t.price}</span>
            {t.original_price && t.original_price > t.price && (
              <span data-testid={`orig-price-${t.slug}`} className="text-sm text-muted-foreground line-through">${t.original_price}</span>
            )}
          </span>
          <Link to={`/templates/${t.slug}/preview`} data-testid={`preview-btn-${t.slug}`}
            className="inline-flex items-center gap-1 rounded-full glass px-3 h-9 text-sm font-semibold hover:text-primary transition-colors"><Eye className="h-4 w-4" /> View</Link>
          <button data-testid={`add-cart-${t.slug}`} onClick={addToCart}
            className={`inline-flex items-center gap-1 rounded-full px-3 h-9 text-sm font-semibold transition-colors ${inCart ? "bg-emerald-500 text-white" : "bg-primary text-primary-foreground hover:brightness-110"}`}>
            {inCart ? <><Check className="h-4 w-4" /> Added</> : <><Plus className="h-4 w-4" /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
};
